// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential. Unauthorized copying, modification,
// or distribution is strictly prohibited.

// ── Where a project opens ────────────────────────────────────────────────────
//
// Three rules, in order, and the order is the whole design:
//
//   1. An explicit destination wins. A deep link, or a snapshot opened to a
//      particular view, asked for somewhere specific and must get it.
//
//   2. Otherwise, where you were last time in THIS project. An estimator
//      living in Buyout for a week paid a navigation tax every morning
//      because the app always opened on the same page regardless of what
//      they were doing. Per project, not global: your buyout project and
//      your brand-new concept estimate are not the same job.
//
//   3. Otherwise, Decisions, if the project has anything outstanding. What
//      is unresolved is what you came back to find out, and it is the one
//      page that answers "where is this" without being read line by line.
//
//   4. Otherwise, Summary -- unless there is nothing to summarise, in which
//      case the estimate grid. An empty summary looks broken; an empty grid
//      looks like a form waiting to be filled.
//
// Decisions never opens empty, because an "unresolved report" that opens
// saying nothing is unresolved has sent you somewhere for no reason.
//
// Every answer is checked against the tabs that actually exist for this user.
// Entitlements gate Bids, RFIs and Cash Flow, so a remembered tab can name a
// page the user can no longer reach -- and landing on a tab that renders
// nothing is worse than ignoring the memory.

const STORE_KEY = "oe_last_tab_v1";

export type LandingInput = {
  /** A destination the caller was told to open. Beats everything. */
  explicit?: string | null;
  /** Where this project was left last time. */
  remembered?: string | null;
  /** Whether the estimate has any priced scope yet. */
  hasItems: boolean;
  /** How many questions are still open on this project. */
  openDecisions?: number;
  /** The tabs reachable for this user, after entitlements. */
  available: readonly string[];
};

export function resolveLandingTab(input: LandingInput): string {
  const { explicit, remembered, hasItems, openDecisions = 0, available } = input;
  const ok = (t: string | null | undefined): t is string =>
    !!t && available.includes(t);

  if (ok(explicit)) return explicit;
  if (ok(remembered)) return remembered;

  if (openDecisions > 0 && ok("decisions")) return "decisions";

  const preferred = hasItems ? "summary" : "estimate";
  if (ok(preferred)) return preferred;
  // Both gone is not a real configuration, but a landing function that can
  // return nothing is a blank screen, so it ends on something.
  return available[0] ?? "estimate";
}

/** True when the estimate has any scope on it at all. */
export function hasPricedScope(
  divisions: any[] | null | undefined,
  itemState: Record<string, any> | null | undefined,
): boolean {
  if (!Array.isArray(divisions) || divisions.length === 0) return false;
  const st = itemState || {};
  // An item counts if it has a description, a quantity or a cost. A fresh
  // project is a full skeleton of empty rows, so counting rows would say
  // every project is already underway.
  for (const v of Object.values(st)) {
    if (!v) continue;
    if (String(v.description ?? "").trim()) return true;
    if (Number(v.qty) > 0) return true;
    if (Number(v.unitCost) > 0) return true;
  }
  return false;
}

type Store = Record<string, string>;

function read(get: (k: string) => string | null): Store {
  try {
    const raw = get(STORE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" ? (parsed as Store) : {};
  } catch {
    return {};
  }
}

/** Where this project was left, or null. */
export function loadLastTab(
  projectId: string | null | undefined,
  get: (k: string) => string | null,
): string | null {
  if (!projectId) return null;
  return read(get)[projectId] ?? null;
}

/**
 * Remember where this project is, keeping the map small.
 *
 * Capped, because this is a convenience and an unbounded map of every project
 * ever opened would sit in storage forever for no benefit. Oldest written out
 * first; being forgotten costs one extra click.
 */
export function saveLastTab(
  projectId: string | null | undefined,
  tab: string,
  get: (k: string) => string | null,
  set: (k: string, v: string) => void,
  limit = 40,
): void {
  if (!projectId || !tab) return;
  const store = read(get);
  if (store[projectId] === tab) return;
  delete store[projectId];
  const entries = Object.entries(store);
  const kept = entries.slice(Math.max(0, entries.length - (limit - 1)));
  kept.push([projectId, tab]);
  try {
    set(STORE_KEY, JSON.stringify(Object.fromEntries(kept)));
  } catch {
    /* storage full or blocked: the memory is a convenience, not the work */
  }
}
